import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Field, Input, Textarea } from '@vmos2/design-system';
import { usePhonePrefix, useSettings } from '@vmos2/providers';
import { validation, parsePhoneNumber, validatePhoneForm } from '@vmos2/utils';
import { PhoneNumberUtil } from 'google-libphonenumber';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';

type Props = {
  Form: Node,
  address?: {
    phoneNumber?: string,
    deliveryInstructions?: string,
  },
  onChange: Function,
  setErrors: Function,
};

const phoneNumberInstance = PhoneNumberUtil.getInstance();

export const DeliveryInstructions = ({ Form, address, onChange, setErrors }: Props) => {
  const { t } = useTranslation('delivery');
  const phonePrefixData = usePhonePrefix();
  const { phonePrefix: defaultPhonePrefix } = useSettings();
  const [phonePrefix, setPhonePrefix] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);

  useEffect(() => {
    if (isNull(phoneNumber)) {
      if (address?.phoneNumber) {
        const phone = parsePhoneNumber(address.phoneNumber);
        setPhonePrefix(phone.prefix ? phone.prefix.toString(): defaultPhonePrefix);
        setPhoneNumber(phone.phone.toString());
      } else {
        setPhonePrefix(defaultPhonePrefix);
      }
    }
  }, [address, phoneNumber, defaultPhonePrefix]);

  const handlePhonePrefix = ({ phonePrefix }) => {
    setPhonePrefix(Number(phonePrefix));
  }
  const handlePhoneChange = (value) => {
    setPhoneNumber(value);
    onChange('phoneNumber', `+${phonePrefix}${value}`);
  };

  const filledInstructions = useMemo(() => ({
    phoneNumber,
    phonePrefix,
  }), [phoneNumber, phonePrefix]);

  const validateFields = ({ phonePrefix, phoneNumber }) => {
    const errors = {};
    let countryCode;
    try {
      const parsedPhoneNumber = phoneNumberInstance.parse(`+${phonePrefix}${phoneNumber}`);
      countryCode = phoneNumberInstance.getRegionCodeForNumber(parsedPhoneNumber);
    } catch (e) {
      countryCode = null;
    }

    if(isEmpty(phoneNumber)) {
      errors.phoneNumber = t('validations:isRequired');
    }

    if (!isEmpty(validatePhoneForm(countryCode, phoneNumber ? phoneNumber.toString() : null, 'phoneNumber'))) {
      errors.phoneNumber = t('validations:invalidMobilePhone');
    }
    return errors;
  };

  useEffect(() => {
    const errors = validateFields({ phonePrefix, phoneNumber });
    setErrors(!isEmpty(errors));

    return () => {
      setErrors(false);
    }
  }, [phonePrefix, phoneNumber]);

  return (
    <Form initialValues={filledInstructions} validate={validateFields}>
      <FormWrapper>
        <Field
          label={t('phoneNumber')}
          phonePrefixData={phonePrefixData}
          phonePrefixDefault={phonePrefix}
          phonePrefixHandleChange={handlePhonePrefix}
          assistiveText={t('phoneAssistiveText')}
          component={Input}
          phonePrefixDisabled
          name="phoneNumber"
          maxLength={12}
          onKeyPress={(event: KeyboardEvent) => {
            if (!event.key.match(/[0-9]/i)) event.preventDefault();
          }}
          size="small"
          validate={[validation.isRequired()]}
          placeholder={t('orderPhoneNumber:placeholder')}
          type="tel"
          pattern="[0-9]*"
          data-test="delivery-phone-number-field"
          mb={6}
          defaultValue={phoneNumber}
          handleChange={handlePhoneChange}
        />

        <Textarea
          name="deiveryInstructions"
          type="textarea"
          size="xsmall"
          fullWidth
          label={t('instructions')}
          defaultValue={address?.deliveryInstructions}
          onChange={({ target: { value }}) => onChange('deliveryInstructions', value)}
          placeholder={t('instructionsPlaceholder')}
        />
      </FormWrapper>
    </Form>
  );
};

const FormWrapper = styled('div')`
  margin-top: 16px;

  @media (${({ theme }) => theme.breakpoint.tabletLarge}) {
    width: 360px;
  }
`;
